import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../services/api";
import { useConfirm } from "../../hooks/useConfirm";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./Reminders.css";

function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, open, done, dismissed
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ message: "", deadline: "", priority: "normal" });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [confirm, confirmDialog] = useConfirm();

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const data = await apiClient.get(`/reminders${params}`);
      setReminders(data.reminders || []);
    } catch (err) {
      console.error("Failed to fetch reminders:", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim() || !form.deadline) return;
    setSaving(true);
    try {
      const deadlineStr = form.deadline.length === 16 ? form.deadline + ":00" : form.deadline;
      await apiClient.post("/reminders", {
        message: form.message,
        deadline: deadlineStr,
        priority: form.priority,
      });
      setForm({ message: "", deadline: "", priority: "normal" });
      setShowForm(false);
      fetchReminders();
    } catch (err) {
      console.error("Failed to create reminder:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleAcknowledge = async (id) => {
    try {
      await apiClient.put(`/reminders/${id}`, { status: "done" });
      fetchReminders();
    } catch (err) {
      console.error("Failed to acknowledge:", err);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await apiClient.put(`/reminders/${id}`, { status: "dismissed" });
      fetchReminders();
    } catch (err) {
      console.error("Failed to dismiss:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm("Delete this reminder?"))) return;
    try {
      await apiClient.delete(`/reminders/${id}`);
      fetchReminders();
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const toggleExpand = (id) => {
    if (editId === id) return; // don't collapse while editing
    setExpandedId((prev) => (prev === id ? null : id));
    setEditId(null);
  };

  const startEdit = (r) => {
    const dl = r.deadline || "";
    setEditForm({
      message: r.message || "",
      deadline: dl ? dl.slice(0, 16) : "",
      priority: r.priority || "normal",
      status: r.status || "open",
    });
    setEditId(r.id);
    setExpandedId(r.id);
  };

  const cancelEdit = () => {
    setEditId(null);
  };

  const handleEditSave = async (id) => {
    setEditSaving(true);
    try {
      const payload = {
        message: editForm.message,
        deadline: editForm.deadline.length === 16 ? editForm.deadline + ":00" : editForm.deadline,
        priority: editForm.priority,
        status: editForm.status,
      };
      await apiClient.put(`/reminders/${id}`, payload);
      setEditId(null);
      fetchReminders();
    } catch (err) {
      console.error("Failed to update reminder:", err);
    } finally {
      setEditSaving(false);
    }
  };

  const formatDateTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  };

  const getDaysUntil = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const dl = new Date(deadline);
    return Math.ceil((dl - now) / (1000 * 60 * 60 * 24));
  };

  const getDaysLabel = (days) => {
    if (days === null) return "";
    if (days < 0) return `${Math.abs(days)}d overdue`;
    if (days === 0) return "Due today";
    if (days === 1) return "Due tomorrow";
    return `${days}d left`;
  };

  const getUrgencyClass = (deadline, priority) => {
    const days = getDaysUntil(deadline);
    if (days === null) return "";
    if (days < 0) return "urgency-overdue";
    if (days <= 1) return "urgency-critical";
    if (days <= 3) return "urgency-high";
    if (days <= 7 && (priority === "critical" || priority === "high")) return "urgency-medium";
    return "";
  };

  const priorityIcon = (p) => {
    if (p === "critical") return "🔴";
    if (p === "high") return "🟠";
    if (p === "low") return "🔵";
    return "🟢";
  };

  const statusIcon = (status) => {
    if (status === "done") return "✅";
    if (status === "dismissed") return "🔕";
    return "🔔";
  };

  const activeReminders = reminders.filter((r) => r.status === "open" || r.status === "pending" || r.status === "sent");
  const doneReminders = reminders.filter((r) => r.status === "done");
  const dismissedReminders = reminders.filter((r) => r.status === "dismissed");

  const renderCard = (r) => {
    const deadline = r.deadline;
    const daysUntil = getDaysUntil(deadline);
    const urgency = getUrgencyClass(deadline, r.priority);
    const isExpanded = expandedId === r.id;
    const isEditing = editId === r.id;

    return (
      <div key={r.id} className={`reminder-card ${urgency} ${r.status === "done" ? "done" : ""} ${r.status === "dismissed" ? "past" : ""}`}>
        <div className="reminder-card-header" onClick={() => toggleExpand(r.id)}>
          <div className="reminder-icon">{r.status === "done" ? "✅" : r.status === "dismissed" ? "🔕" : priorityIcon(r.priority)}</div>
          <div className="reminder-body">
            <div className="reminder-message">
              {r.message}
            </div>
            <div className="reminder-meta">
              <span className="reminder-deadline">{formatDate(deadline)}</span>
              {r.status === "open" && (
                <span className={`reminder-days ${daysUntil !== null && daysUntil < 0 ? "overdue" : ""}`}>
                  {getDaysLabel(daysUntil)}
                </span>
              )}
              <span className="reminder-priority">{r.priority}</span>
            </div>
          </div>
          <div className="reminder-actions" onClick={(e) => e.stopPropagation()}>
            {r.status === "open" && (
              <>
                <button className="btn-success btn-sm" onClick={() => handleAcknowledge(r.id)} title="Mark as done">✓ Done</button>
                <button className="btn-secondary btn-sm" onClick={() => handleDismiss(r.id)} title="Dismiss">Dismiss</button>
              </>
            )}
            <button className="btn-danger btn-sm" onClick={() => handleDelete(r.id)} title="Delete">✕</button>
          </div>
        </div>

        {isExpanded && !isEditing && (
          <div className="reminder-detail">
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Status</span>
                <span className="detail-value">{statusIcon(r.status)} {r.status}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Priority</span>
                <span className="detail-value">{priorityIcon(r.priority)} {r.priority}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Deadline</span>
                <span className="detail-value">{formatDateTime(deadline)}</span>
              </div>
              {r.scheduleId && (
                <div className="detail-item">
                  <span className="detail-label">Linked schedule</span>
                  <span className="detail-value">{r.scheduleId}</span>
                </div>
              )}
            </div>
            <div className="detail-actions">
              <button className="btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); startEdit(r); }}>✏️ Edit</button>
            </div>
          </div>
        )}

        {isEditing && (
          <div className="reminder-edit" onClick={(e) => e.stopPropagation()}>
            <div className="edit-form">
              <div className="form-group">
                <label>Message</label>
                <input
                  value={editForm.message}
                  onChange={(e) => setEditForm({ ...editForm, message: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Deadline</label>
                  <input
                    type="datetime-local"
                    value={editForm.deadline}
                    onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={editForm.priority}
                    onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                  >
                    <option value="low">🔵 Low</option>
                    <option value="normal">🟢 Normal</option>
                    <option value="high">🟠 High</option>
                    <option value="critical">🔴 Critical</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    <option value="open">🔔 Open</option>
                    <option value="done">✅ Done</option>
                    <option value="dismissed">🔕 Dismissed</option>
                  </select>
                </div>
              </div>
              <div className="edit-actions">
                <button className="btn-primary btn-sm" onClick={() => handleEditSave(r.id)} disabled={editSaving}>
                  {editSaving ? "Saving..." : "Save"}
                </button>
                <button className="btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="reminders-page">
      {confirmDialog}
      <div className="reminders-header">
        <h2>Reminders</h2>
        <div className="reminders-actions">
          <div className="filter-group">
            {["all", "open", "done", "dismissed"].map((f) => (
              <button
                key={f}
                className={filter === f ? "btn-primary btn-sm" : "btn-secondary btn-sm"}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <RefreshButton onClick={fetchReminders} title="Refresh reminders" />
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? "Cancel" : "+ New Reminder"}
          </button>
        </div>
      </div>

      {showForm && (
        <form className="reminder-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <input
              id="message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="What do you want to be reminded about?"
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="deadline">Deadline</label>
              <input
                id="deadline"
                type="datetime-local"
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <option value="low">🔵 Low</option>
                <option value="normal">🟢 Normal</option>
                <option value="high">🟠 High</option>
                <option value="critical">🔴 Critical</option>
              </select>
            </div>
          </div>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Create Reminder"}
          </button>
        </form>
      )}

      {loading ? (
        <div className="reminders-loading">Loading reminders...</div>
      ) : reminders.length === 0 ? (
        <div className="reminders-empty">
          <p>🔔 No reminders yet</p>
          <p className="text-muted">
            Ask Anna to create reminders for you, or click "+ New Reminder" above.
          </p>
        </div>
      ) : (
        <div className="reminders-list">
          {activeReminders.length > 0 && (
            <>
              <h3 className="section-label">Active</h3>
              {activeReminders
                .sort((a, b) => (a.deadline || "").localeCompare(b.deadline || ""))
                .map(renderCard)}
            </>
          )}
          {doneReminders.length > 0 && (
            <>
              <h3 className="section-label">Done</h3>
              {doneReminders
                .sort((a, b) => (b.deadline || "").localeCompare(a.deadline || ""))
                .map(renderCard)}
            </>
          )}
          {dismissedReminders.length > 0 && (
            <>
              <h3 className="section-label">Dismissed</h3>
              {dismissedReminders
                .sort((a, b) => (b.deadline || "").localeCompare(a.deadline || ""))
                .map(renderCard)}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Reminders;
